import React from 'react';
import { IGlobalErrorBoundaryProps, IGlobalErrorBoundaryState } from './types';

/**
 * React error boundary enclosing the entire application.
 * Smaller components in the app should have their own error boundaries as appropriate.
 * Any uncaught rendering/React lifecycle errors will be handled here.
 * https://reactjs.org/docs/error-boundaries.html
 * @extends React.Component
 */
export class GlobalErrorBoundary extends React.Component<IGlobalErrorBoundaryProps, IGlobalErrorBoundaryState> {
  static defaultProps: Partial<IGlobalErrorBoundaryProps> = {
    logError: null,
    renderErrorView: null,
    errorViewProps: null,
  };

  constructor(props: IGlobalErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (this.props.logError) {
      this.props.logError({ error, info });
    }
    this.setState({ hasError: true, error, info });
  }

  render(): React.ReactNode {
    // Set renderErrorView to null to swallow the error instead of crashing the page
    if (this.state.hasError && this.props.renderErrorView) {
      return this.props.renderErrorView({
        error: this.state.error,
        info: this.state.info,
        errorViewProps: this.props.errorViewProps,
      });
    }
    return (
      <>
        {this.props.children}
        <div className="global-error-boundary" style={{ display: 'none' }} />
      </>
    );
  }
}
